// Shared access-log helpers: who checked someone in/out, and pass overstays.
import { enumKey, enumOf } from "./enums";

const PASS_EXPIRY_KEYS = ["accessCodeExpiresAt", "codeExpiresAt", "passExpiresAt", "expiresAt"];
const PASS_PERMANENT_KEYS = ["accessCodeIsPermanent", "isAccessCodePermanent", "isPermanentAccessCode", "isPermanent"];

function firstDefined(obj, keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

function titleCase(str) {
  return String(str)
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

// Enum fields on access-log rows. The API sends a raw value and, on newer responses,
// a readable `<field>Text` beside it. Resolve those once so the rest of the app can keep
// reading the plain field names.
const LOG_TYPE_FIELDS = ["subjectType", "dependentType", "checkedInByType", "checkedOutByType", "accessCodeCreatedByType", "hostType", "actorType"];
const LOG_LABEL_FIELDS = ["verificationMethod", "direction", "decision", "accessMode"];

export function normalizeAccessLog(log) {
  if (!log || typeof log !== "object") return log;
  const out = { ...log };
  // Type fields are compared as identifiers ("Tenant", "PrimaryOccupant", "Dependent").
  LOG_TYPE_FIELDS.forEach((f) => {
    const v = enumOf(log, f);
    if (v !== undefined) out[f] = typeof v === "string" ? v.replace(/[\s_-]+/g, "") : v;
  });
  // Label fields are shown as-is.
  LOG_LABEL_FIELDS.forEach((f) => {
    const v = enumOf(log, f);
    if (v !== undefined) out[f] = v;
  });
  return out;
}

// The vehicle a person arrived in. Logs carry it as `checkInVehicle` (or `vehicle` on audit
// rows) with { licensePlate, manufacturer, make, model, color, isUnlistedVehicle }; older rows
// used flat vehicleNumber / vehicleModel / vehicleColor fields. Returns null when they came on foot
// or the log says nothing about a vehicle.
export function getLogVehicle(log) {
  if (!log) return null;
  const mode = enumKey(enumOf(log, "checkInAccessMode") || enumOf(log, "accessMode"));
  const v = log.checkInVehicle || log.vehicle || log.checkOutVehicle || null;
  const plate = v?.licensePlate || log.vehicleNumber || null;
  const brand = v?.manufacturer || v?.make || null;
  const model = v?.model || v?.make || log.vehicleModel || null;
  const color = v?.color || log.vehicleColor || null;
  const isUnlisted = v ? v.isUnlistedVehicle === true : false;
  const hasDetails = Boolean(plate || brand || model || color);
  if (!hasDetails && !isUnlisted && mode !== "vehicle" && !log.hasVehicle) return null;
  return {
    plate,
    brand,
    model,
    color,
    isUnlisted: isUnlisted && !hasDetails,
    imageUrl: log.vehicleImageUrl || null,
    vehicleId: v?.vehicleId || null,
  };
}

// "by Abdulrasaq Sanni" for the officer who checked the person in ("in") or out ("out").
export function officerLabel(log, direction) {
  const isIn = direction === "in";
  const name = isIn
    ? firstDefined(log, ["checkedInByName", "checkedInBySecurityName", "checkedInByOfficerName", "checkedByName"])
    : firstDefined(log, ["checkedOutByName", "checkedOutBySecurityName", "checkedOutByOfficerName"]);
  if (name && String(name).trim()) return `by ${titleCase(String(name).trim())}`;

  const type = isIn ? log?.checkedInByType : log?.checkedOutByType;
  if (type) {
    const t = String(type).replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
    if (/security|officer|guard|personnel/.test(t)) return "by security";
    if (t === "tenant") return "by resident";
    return `by ${t}`;
  }
  return "Officer not recorded";
}

// Only dependants on a temporary (time-limited) pass can overstay. Residents, primary
// occupants and permanent passes never do. `passInfo` maps a log id to
// { isPermanent, expiresAt } for passes looked up separately (see fetchPassInfo).
export function getPassLimitMs(log, passInfo) {
  if (!log || log.subjectType !== "Dependent") return null;
  const info = passInfo?.[log.baseId || log.id] || {};
  const isPermanent = info.isPermanent ?? firstDefined(log, PASS_PERMANENT_KEYS);
  if (isPermanent === true) return null;
  const expiresAt = info.expiresAt || firstDefined(log, PASS_EXPIRY_KEYS);
  if (!expiresAt) return null;
  const t = new Date(expiresAt).getTime();
  return Number.isNaN(t) ? null : t;
}

export function isPermanentPass(log, passInfo) {
  const info = passInfo?.[log?.baseId || log?.id] || {};
  return (info.isPermanent ?? firstDefined(log, PASS_PERMANENT_KEYS)) === true;
}

// Residents: tenants, primary occupants and dependants on a permanent pass.
// Everyone else (temporary dependants, visitors) counts as a visitor.
export function isResidentLog(log, passInfo) {
  const type = String(log?.subjectType || "").toLowerCase().replace(/[^a-z]/g, "");
  if (type === "tenant" || type === "primaryoccupant") return true;
  if (type === "dependent") return isPermanentPass(log, passInfo);
  return false;
}

// Visitors: dependants whose pass is known to be temporary, plus any non-resident
// subject type (e.g. walk-in visitors). Dependants whose pass is not loaded yet
// are counted in neither group until it is.
export function isTemporaryPassLog(log, passInfo) {
  const type = String(log?.subjectType || "").toLowerCase().replace(/[^a-z]/g, "");
  if (type === "tenant" || type === "primaryoccupant") return false;
  if (type === "dependent") {
    const info = passInfo?.[log?.baseId || log?.id] || {};
    return (info.isPermanent ?? firstDefined(log, PASS_PERMANENT_KEYS)) === false;
  }
  return Boolean(type);
}

export function checkIsOverstay(log, passInfo) {
  if (!log || !log.checkedInAt || log.checkedOutAt) return false;
  // The API flags overstays itself; fall back to the pass expiry only on rows without the flag.
  if (typeof log.isOverstay === "boolean") return log.isOverstay;
  const limit = getPassLimitMs(log, passInfo);
  return limit !== null && Date.now() > limit;
}

// Access logs may not include pass details. For dependants without them, look up their
// access codes and match the code they entered with. By default only people still
// inside are looked up; pass includeCheckedOut to cover everyone in `logs`.
export async function fetchPassInfo({ apiBase, estateId, logs, authFetch, includeCheckedOut = false, skip = {} }) {
  if (!estateId || !Array.isArray(logs)) return {};
  const pending = logs.filter((l) =>
    l.subjectType === "Dependent" &&
    !skip[l.id] &&
    (includeCheckedOut || !l.checkedOutAt) &&
    firstDefined(l, PASS_EXPIRY_KEYS) === undefined &&
    firstDefined(l, PASS_PERMANENT_KEYS) === undefined
  );
  if (pending.length === 0) return {};

  // One lookup per dependant + code, at most 8 requests in flight.
  const lookups = new Map();
  pending.forEach((l) => {
    const key = `${l.dependentId || l.subjectId}|${l.code || l.accessCode || ""}`;
    if (!lookups.has(key)) lookups.set(key, []);
    lookups.get(key).push(l);
  });

  const lookupOne = async (l) => {
    const dependentId = l.dependentId || l.subjectId;
    if (!dependentId || !l.tenantId) return null;
    const isPoHost = String(l.hostType || l.accessCodeCreatedByType || "").toLowerCase().replace(/[^a-z]/g, "") === "primaryoccupant";
    const poId = l.primaryOccupantId || l.hostId;
    const base = `${apiBase}/estates/${estateId}/tenants/${l.tenantId}`;
    const url = isPoHost && poId
      ? `${base}/primary-occupants/${poId}/dependents/${dependentId}/access-codes?pageIndex=1&pageSize=50`
      : `${base}/dependents/${dependentId}/access-codes?pageIndex=1&pageSize=50`;
    try {
      const res = await authFetch(url);
      if (!res.ok) return null;
      let json = null;
      try { json = JSON.parse(await res.text()); } catch { return null; }
      const codes = Array.isArray(json?.data?.data) ? json.data.data : Array.isArray(json?.data) ? json.data : [];
      const used = l.code || l.accessCode;
      const match = codes.find((c) => used && c.code === used) || (codes.length === 1 ? codes[0] : null);
      if (!match) return null;
      return { isPermanent: match.isPermanent === true, expiresAt: match.expiresAt || null };
    } catch {
      return null;
    }
  };

  const groups = [...lookups.values()];
  const results = [];
  let next = 0;
  const worker = async () => {
    while (next < groups.length) {
      const group = groups[next++];
      const info = await lookupOne(group[0]);
      if (info) group.forEach((l) => results.push([l.id, info]));
    }
  };
  await Promise.all(Array.from({ length: Math.min(8, groups.length) }, worker));
  return Object.fromEntries(results);
}
